import { AxiosResponse } from "axios";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import {
  DutyTaxConfigurationListItem,
  DutyTaxConfigurationListResult,
  DutyTaxConfigurationStatus,
  DutyTaxResolveResult
} from "@/domain/entities/DutyTaxConfiguration";
import { PaginationV2 } from "@/domain/entities/Pagination";

function normalizeStatus(status: string): DutyTaxConfigurationStatus {
  return String(status).toLowerCase() === "active" ? "active" : "inactive";
}

export class DutyTaxConfigurationMapper {
  convertListFromApi(result: AxiosResponse): DutyTaxConfigurationListResult {
    const body = result.data ?? {};
    const rows = Array.isArray(body.data) ? body.data : [];
    const pagination = body.meta ?? {};

    const data: DutyTaxConfigurationListItem[] = rows.map(
      (item: Record<string, unknown>) => {
        const row = convertSnakeToCamel(item) as Record<string, unknown>;
        return {
          no: Number(row.no ?? 0),
          destinationCountryCode: String(row.destinationCountryCode ?? ""),
          destinationCountryName: String(row.destinationCountryName ?? ""),
          commodityCode: String(row.commodityCode ?? ""),
          commodityName: String(row.commodityName ?? ""),
          minWeightKg: Number(row.minWeightKg ?? 0),
          maxWeightKg: Number(row.maxWeightKg ?? 0),
          deminimisValue: Number(row.deminimisValue ?? 0),
          destinationCurrencyCode: String(row.destinationCurrencyCode ?? ""),
          documentCircularRequired: Boolean(row.documentCircularRequired),
          selfClearanceRequired: Boolean(row.selfClearanceRequired),
          status: normalizeStatus(String(row.status ?? "inactive")),
          createdBy: String(row.createdBy ?? ""),
          createdAt: String(row.createdAt ?? ""),
          updatedBy: (row.updatedBy as string) ?? "",
          updatedAt: (row.updatedAt as string) ?? ""
        };
      }
    );

    return {
      data,
      pagination: new PaginationV2({
        page: Number(pagination.page ?? 1),
        limit: Number(pagination.limit ?? 20),
        totalData: Number(pagination.total_records ?? 0),
        allTotalData: Number(pagination.total_records ?? 0)
      })
    };
  }

  // 200 with `data: null` or an empty object both mean "no match" — return
  // null so callers never render an infobar for an absent/failed resolve.
  convertResolveFromApi(result: AxiosResponse): DutyTaxResolveResult | null {
    const body = result.data ?? {};
    const data = body.data;
    if (!data || Object.keys(data).length === 0) return null;

    const row = convertSnakeToCamel(data) as Record<string, unknown>;
    const weightRange = row.matchedWeightRange as
      | Record<string, unknown>
      | undefined;

    return {
      deminimisValue: Number(row.deminimisValue ?? 0),
      destinationCurrencyCode: String(row.destinationCurrencyCode ?? ""),
      deminimisExceeded: Boolean(row.deminimisExceeded),
      documentCircularRequired: Boolean(row.documentCircularRequired),
      selfClearanceRequired: Boolean(row.selfClearanceRequired),
      matchedCommodityCode: String(row.matchedCommodityCode ?? ""),
      matchedWeightRange: weightRange
        ? {
            minWeightKg: Number(weightRange.minWeightKg ?? 0),
            maxWeightKg: Number(weightRange.maxWeightKg ?? 0)
          }
        : null
    };
  }
}
